import { lazy, type ComponentType } from 'react';

export interface LazyScreen {
  readonly Screen: ComponentType;
  /** Starts downloading the screen's code; safe to call any number of times. */
  readonly preload: () => void;
}

/**
 * A screen whose code is split from the home page. The home page renders
 * first; the screens are fetched while the browser is idle, so opening one
 * normally finds its code already there.
 */
export function lazyScreen(load: () => Promise<ComponentType>): LazyScreen {
  let pending: Promise<{ default: ComponentType }> | null = null;
  const fetchOnce = () => {
    pending ??= load().then(
      (Screen) => ({ default: Screen }),
      (error: unknown) => {
        pending = null; // A dropped connection must not break the screen for good.
        throw error;
      },
    );
    return pending;
  };
  return { Screen: lazy(fetchOnce), preload: () => void fetchOnce().catch(() => undefined) };
}

/** Runs `task` when the main thread is idle (or soon, where idle callbacks don't exist). */
export function whenIdle(task: () => void): () => void {
  if (typeof requestIdleCallback === 'function') {
    const handle = requestIdleCallback(task, { timeout: 4000 });
    return () => cancelIdleCallback(handle);
  }
  const handle = window.setTimeout(task, 1500);
  return () => window.clearTimeout(handle);
}
